import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useMediaQuery, useTheme } from '@mui/material';

import { LAYER_NORM_CUSTOM, LAYER_NORM_NONE } from '../../constants/layers.ts';
import { useModelContext } from '../../context/modelContext';
import { useAppDispatch, useAppSelector } from '../../hooks.ts';
import { projectsReducer } from '../../store/reducers/projects.reducer.ts';
import { getFolderSelector, getNewProjectSelector } from '../../store/selectors/projects.selector.ts';
import { getUploadProgress } from '../../store/selectors/ui.selector.ts';
import { FileMetadataRequest, FileType } from '../../types/file.ts';
import { Mesh, MeshGroup } from '../../types/mesh.ts';
import { NewProjectRequest } from '../../types/project.ts';
import { rescaleModelBlob } from '../../utils/rescaleModel';
import { FileUploadProgress } from '../loading/FileUploadProgress.tsx';
import { ModelLayer } from '../model-context/ModelContext.tsx';
import { ModelPreviewScene } from '../model-preview/ModelPreviewScene.tsx';
import { NewLayerSidebar } from '../project-settings/components/LayerSidebar';
import { useLayerSettings } from '../project-settings/hooks/useLayerSettings';
import { LAYER_TYPES, LayerSetting, ProjectSettingsProps } from '../project-settings/types';
import { NewProjectLayerPopup, NewProjectLayerPopupData } from './NewProjectLayerPopup.tsx';

const { setNewProject, createProject } = projectsReducer.actions;

export default function ProjectSettings({ onBack }: ProjectSettingsProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const captureImageFunc = useRef<(() => string) | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useAppDispatch();
  const { uploadedModels, modelLayers, addModelLayers, setModelLayers, metersPerUnit } = useModelContext();
  const [selectedLayer, setSelectedLayer] = useState<string | null>(null);
  const [hoveredLayer, setHoveredLayer] = useState<string | null>(null);
  const [hoveredLayerColor, setHoveredLayerColor] = useState<string | null>(null);
  const [loadedModels, setLoadedModels] = useState<Record<string, boolean>>({});
  const [_validationError, setValidationError] = useState<string | null>(null);
  const [previewImageBase64, setPreviewImageBase64] = useState<string>('');
  const [isLayersValid, setIsLayersValid] = useState<boolean>(true);
  const [isCapturing, setIsCapturing] = useState<boolean>(false);
  const [meshGroups, setMeshGroups] = useState<MeshGroup[]>([]);
  const [layerPopup, setLayerPopup] = useState<NewProjectLayerPopupData | null>(null);

  // Drag detection so camera rotation doesn't open the popup
  const pointerDownPos = useRef<{ x: number; y: number } | null>(null);
  const pointerTravel = useRef(0);
  const DRAG_THRESHOLD = 5;

  // Orbit cursor indicator — white circle follows cursor during any camera drag
  const [orbitCursor, setOrbitCursor] = useState<{ x: number; y: number } | null>(null);
  const mouseButtonDown = useRef(false);

  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      // Only track camera-drag feedback when the gesture starts on the 3D canvas,
      // not on overlays (sidebar, toolbars, modals) that sit above it.
      if (!(e.target instanceof HTMLCanvasElement)) return;
      pointerDownPos.current = { x: e.clientX, y: e.clientY };
      pointerTravel.current = 0;
      if (e.button === 0 || e.button === 2) {
        mouseButtonDown.current = true;
      }
    };
    const onMove = (e: PointerEvent) => {
      if (!pointerDownPos.current) return;
      const dx = e.clientX - pointerDownPos.current.x;
      const dy = e.clientY - pointerDownPos.current.y;
      pointerTravel.current = Math.sqrt(dx * dx + dy * dy);

      // Show orbit cursor when dragging past threshold (camera orbit/pan)
      if (mouseButtonDown.current && pointerTravel.current > DRAG_THRESHOLD) {
        setOrbitCursor({ x: e.clientX, y: e.clientY });
      }
    };
    const onUp = () => {
      mouseButtonDown.current = false;
      setOrbitCursor(null);
    };
    window.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, []);

  const project = useAppSelector(getNewProjectSelector);
  const uploadProgress = useAppSelector(getUploadProgress);
  const isUploading = !!uploadProgress;
  const folder = useAppSelector(getFolderSelector);

  const { layerSettings, handleToggleChange, handleNormChange, handleCustomTargets, handleTypeChange } =
    useLayerSettings(modelLayers);

  const getLayers = () => {
    if (project.layers.length === 0 && layerSettings.length !== 0) {
      dispatch(
        setNewProject({
          ...project,
          layers: layerSettings,
        }),
      );
      return layerSettings;
    }
    return project.layers;
  };

  const handleIlluminatedClick = (name: string, field: 'illuminated' | 'canHoldLight', value: boolean) => {
    handleToggleChange(name, field, value);
    const updatedLayers = project.layers.map((layer) => {
      if (layer.threeJsName === name) {
        if (field === 'illuminated' && !value) {
          return { ...layer, [field]: value, norm: LAYER_NORM_NONE, customTargets: null };
        }
        return { ...layer, [field]: value };
      }
      return layer;
    });
    const updatedProject = {
      ...project,
      layers: updatedLayers,
    };
    dispatch(setNewProject(updatedProject));
  };

  const handleNormSelect = (layerName: string, value: string) => {
    handleNormChange(layerName, value);
    const updatedLayers = project.layers.map((layer) =>
      layer.threeJsName === layerName ? { ...layer, norm: value, customTargets: null } : layer,
    );
    const updatedProject = {
      ...project,
      layers: updatedLayers,
    };
    dispatch(setNewProject(updatedProject));
  };

  const handleCustomTargetsChange = (layerName: string, targets: NonNullable<LayerSetting['customTargets']>) => {
    handleCustomTargets(layerName, targets);
    const updatedLayers = project.layers.map((layer) =>
      layer.threeJsName === layerName
        ? targets.length
          ? { ...layer, norm: LAYER_NORM_CUSTOM, customTargets: targets, illuminated: true }
          : { ...layer, norm: LAYER_NORM_NONE, customTargets: null }
        : layer,
    );
    dispatch(setNewProject({ ...project, layers: updatedLayers }));
  };

  const handleTypeSelect = (layerName: string, type: keyof typeof LAYER_TYPES) => {
    handleTypeChange(layerName, type);
    const updatedLayers = project.layers.map((layer) =>
      layer.threeJsName === layerName
        ? {
            ...layer,
            type: type,
            color: type !== 'Unspecified' ? LAYER_TYPES[type] : layer.color,
          }
        : layer,
    );
    const updatedProject = {
      ...project,
      layers: updatedLayers,
    };
    dispatch(setNewProject(updatedProject));
  };

  const handleObjectHover = (name: string | null) => {
    setHoveredLayer(name);
  };

  // Track whether the popup is consuming a pointer event (prevents canvas click-through)
  const popupInteracting = useRef(false);

  // Left-click on a mesh → toggle selection (no popup)
  const handleObjectSelect = (name: string) => {
    if (popupInteracting.current) {
      popupInteracting.current = false;
      return;
    }

    // If the user dragged (camera rotation), don't select
    const wasDrag = pointerTravel.current > DRAG_THRESHOLD;
    pointerDownPos.current = null;
    pointerTravel.current = 0;
    if (wasDrag) return;

    if (selectedLayer === name) {
      setSelectedLayer(null);
    } else {
      setSelectedLayer(name);
    }
  };

  // Right-click on a mesh → open popup (called by RightClickHandler)
  const handleObjectClick = (name: string) => {
    setSelectedLayer(name);

    // Get the mouse position from the latest pointer event
    const lastEvent = window.event as MouseEvent | undefined;
    const screenX = lastEvent?.clientX ?? window.innerWidth / 2;
    const screenY = lastEvent?.clientY ?? window.innerHeight / 2;

    setLayerPopup({ layerName: name, screenX, screenY });
  };

  const handleCaptureImage = useCallback(async (): Promise<string> => {
    // Use the capture function from the Three.js scene if available
    if (captureImageFunc.current) {
      setIsCapturing(true);

      // Wait for the next animation frames to ensure Three.js has rendered
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

      const base64 = captureImageFunc.current();

      setIsCapturing(false);
      return base64;
    }

    // Fallback to canvas capture if callback not available
    if (!containerRef.current) return '';

    const canvas = containerRef.current.querySelector('canvas');
    if (!canvas) return '';

    const dataURL = canvas.toDataURL('image/png', 1.0);
    const base64 = dataURL.split(',')[1];

    return base64;
  }, []);

  const handleLayersFound = useCallback(
    async (modelName: string, layers: ModelLayer[]) => {
      // Mark this model as loaded
      setLoadedModels((prev) => {
        const newLoadedModels = {
          ...prev,
          [modelName]: true,
        };

        const allModelsLoaded = uploadedModels?.every((model) => newLoadedModels[model.name]) ?? false;

        if (allModelsLoaded && uploadedModels && uploadedModels.length > 0) {
          setTimeout(async () => {
            const imageBase64 = await handleCaptureImage();
            setPreviewImageBase64(imageBase64);
          }, 100);
        }

        return newLoadedModels;
      });

      addModelLayers(
        modelName,
        layers.map((layer) => layer.threeJsName),
        layers.map((layer) => layer.originalName),
      );
    },
    [uploadedModels, addModelLayers, handleCaptureImage],
  );

  const handleSelectedLayer = (layerName: string | null, colorHash: string | null) => {
    setHoveredLayer(layerName);
    setHoveredLayerColor(colorHash);
  };

  const handleCaptureReady = useCallback((captureFunc: () => string) => {
    captureImageFunc.current = captureFunc;
  }, []);

  function convertLayerToMesh(layer: LayerSetting): Mesh {
    const custom = layer.illuminated && layer.customTargets?.length ? layer.customTargets : undefined;
    const normName =
      !custom && layer.illuminated && layer.norm !== LAYER_NORM_NONE && layer.norm !== LAYER_NORM_CUSTOM
        ? layer.norm
        : undefined;

    return {
      uuid: layer.uuid,
      groupId: layer.groupId,
      name: layer.originalName,
      displayName: layer.displayName,
      color: layer.color,
      type: layer.type.toUpperCase(),
      normName: normName,
      targets: custom,
      canHoldLight: layer.canHoldLight,
      desiredLuminaireHeight: layer.desiredLuminaireHeight,
    };
  }

  function convertLayersToMeshes(layers: LayerSetting[]): Mesh[] {
    return layers.map((layer) => convertLayerToMesh(layer));
  }

  const handleBackClick = () => {
    // Clear layer settings when going back
    dispatch(
      setNewProject({
        ...project,
        layers: [],
      }),
    );
    // Clear model layers as well
    setModelLayers([]);
    onBack();
  };

  const handleAddMeshGroup = (parentGroupId?: string | null) => {
    const newGroup: MeshGroup = {
      uuid: crypto.randomUUID(),
      name: t('meshGroups.newGroup'),
      subgroups: [],
    };
    if (!parentGroupId) {
      setMeshGroups((prev) => [...prev, newGroup]);
    } else {
      const addToParent = (groups: MeshGroup[]): MeshGroup[] =>
        groups.map((g) =>
          g.uuid === parentGroupId
            ? { ...g, subgroups: [...g.subgroups, newGroup] }
            : { ...g, subgroups: addToParent(g.subgroups) },
        );
      setMeshGroups((prev) => addToParent(prev));
    }
  };

  const handleRenameMeshGroup = (uuid: string, name: string) => {
    const rename = (groups: MeshGroup[]): MeshGroup[] =>
      groups.map((g) => (g.uuid === uuid ? { ...g, name } : { ...g, subgroups: rename(g.subgroups) }));
    setMeshGroups((prev) => rename(prev));
  };

  const handleDeleteMeshGroup = (uuid: string) => {
    const collectIds = (groups: MeshGroup[]): string[] => groups.flatMap((g) => [g.uuid, ...collectIds(g.subgroups)]);
    const findGroup = (groups: MeshGroup[]): MeshGroup | null => {
      for (const g of groups) {
        if (g.uuid === uuid) return g;
        const found = findGroup(g.subgroups);
        if (found) return found;
      }
      return null;
    };
    const group = findGroup(meshGroups);
    if (group) {
      const deletedIds = new Set(collectIds([group]));
      // Move layers in deleted groups to root
      const updatedLayers = project.layers.map((layer) =>
        layer.groupId && deletedIds.has(layer.groupId) ? { ...layer, groupId: null } : layer,
      );
      dispatch(setNewProject({ ...project, layers: updatedLayers }));
    }
    const removeGroup = (groups: MeshGroup[]): MeshGroup[] =>
      groups.filter((g) => g.uuid !== uuid).map((g) => ({ ...g, subgroups: removeGroup(g.subgroups) }));
    setMeshGroups((prev) => removeGroup(prev));
  };

  const handleLayerGroupChange = (layerUuid: string, groupId: string | null) => {
    const updatedLayers = project.layers.map((layer) => (layer.uuid === layerUuid ? { ...layer, groupId } : layer));
    dispatch(setNewProject({ ...project, layers: updatedLayers }));
  };

  const handleCreate = async () => {
    // Validate that illuminated layers have a norm selected
    const illuminatedLayersWithoutNorm = project.layers.filter(
      (layer) => layer.illuminated && (!layer.norm || layer.norm === LAYER_NORM_NONE),
    );

    if (illuminatedLayersWithoutNorm.length > 0) {
      setValidationError(t('projects.newProject.validation.illuminatedNormRequired'));
      // Scroll to the error message if needed
      return;
    }

    // Clear any previous validation errors
    setValidationError(null);

    // Use the already captured preview image or capture one if not available
    const imageBase64 = previewImageBase64 || (await handleCaptureImage());

    const { layers, ...restOfProject } = project;
    const props: FileMetadataRequest[] = (uploadedModels ?? []).map((modelData) => ({
      name: modelData.name,
      fileType: FileType.OBJECT_3D,
    }));

    const request: NewProjectRequest = {
      ...restOfProject,
      name: restOfProject.name.trim() === '' ? t('common.untitled') : restOfProject.name,
      meshes: convertLayersToMeshes(layers),
      meshGroups: meshGroups,
      props,
      previewImageBase64: imageBase64,
      folderId: folder?.id,
    };

    const formData = new FormData();
    const json = JSON.stringify(request);
    const blob = new Blob([json], { type: 'application/json' });
    formData.append('project', blob);

    const fetchPromises: Promise<void>[] = [];
    // If the user calibrated the model scale, bake metersPerUnit into each file so
    // the backend receives geometry in meters (1 unit = 1 m).
    const needsRescale = Math.abs(metersPerUnit - 1) > 1e-9;

    uploadedModels?.forEach((modelData) => {
      if (!modelData.url) return;

      const fetchPromise = (async () => {
        let blob: Blob;
        let contentType = modelData.type || 'application/octet-stream';
        if (needsRescale) {
          blob = await rescaleModelBlob(modelData.url!, modelData.name, metersPerUnit);
          contentType = blob.type || contentType;
        } else {
          const resp = await fetch(modelData.url!);
          blob = await resp.blob();
        }
        const file = new File([blob], modelData.name, {
          type: contentType,
          lastModified: modelData.lastModified || Date.now(),
        });
        formData.append('models', file);
      })();

      fetchPromises.push(fetchPromise);
    });

    await Promise.all(fetchPromises);
    dispatch(createProject(formData));
  };

  return (
    <div className="fixed inset-0 w-full h-full bg-[#111827]">
      {isUploading && <FileUploadProgress uploadProgress={uploadProgress} />}
      <div
        style={{
          position: 'fixed',
          top: 64,
          left: 0,
          right: isMobile ? 0 : 455,
          bottom: 0,
          zIndex: 0,
        }}
        ref={containerRef}
      >
        <ModelPreviewScene
          onObjectHover={handleObjectHover}
          onObjectClick={handleObjectClick}
          onObjectSelect={handleObjectSelect}
          hoveredLayer={hoveredLayer}
          selectedLayer={selectedLayer}
          layerSettings={getLayers()}
          hoveredLayerColor={hoveredLayerColor}
          uploadedModels={uploadedModels}
          visibleLayers={getLayers().map((layer) => layer.threeJsName)}
          onLayersFound={handleLayersFound}
          loadedModels={loadedModels}
          isCapturing={isCapturing}
          onCaptureReady={handleCaptureReady}
          modelScale={metersPerUnit}
        />
      </div>

      {/* Orbit cursor indicator — white circle follows cursor during right-click drag */}
      {orbitCursor && (
        <div
          style={{
            position: 'fixed',
            left: orbitCursor.x - 35,
            top: orbitCursor.y - 35,
            width: 70,
            height: 70,
            borderRadius: '50%',
            border: '3px solid rgba(255, 255, 255, 0.5)',
            pointerEvents: 'none',
            zIndex: 40,
          }}
        />
      )}

      {/* Layer quick-edit popup on 3D canvas click */}
      {layerPopup && (
        <NewProjectLayerPopup
          data={layerPopup}
          layerSettings={getLayers()}
          onToggleChange={handleIlluminatedClick}
          onNormChange={handleNormSelect}
          onCustomTargets={handleCustomTargetsChange}
          onClose={() => setLayerPopup(null)}
          onPointerDown={() => {
            popupInteracting.current = true;
          }}
        />
      )}

      <div style={{ position: 'relative', zIndex: 1, height: '100%', pointerEvents: 'none' }}>
        <div className="fixed top-[64px] bottom-0 right-0 h-[calc(100vh-64px)] w-full md:w-[455px] bg-[#232323] text-gray-300 flex flex-col z-10 shadow-2xl md:rounded-l-2xl pointer-events-auto">
          <div className="flex-1 overflow-hidden min-h-0">
            <NewLayerSidebar
              isMobile={isMobile}
              layerSettings={getLayers()}
              selectedLayer={selectedLayer}
              hoveredLayer={hoveredLayer}
              onHover={setHoveredLayer}
              onSelect={setSelectedLayer}
              onTypeChange={handleTypeSelect}
              onToggleChange={handleIlluminatedClick}
              onNormChange={handleNormSelect}
              onCustomTargets={handleCustomTargetsChange}
              onLayerHover={handleSelectedLayer}
              onValidationChange={setIsLayersValid}
              meshGroups={meshGroups}
              onAddMeshGroup={handleAddMeshGroup}
              onRenameMeshGroup={handleRenameMeshGroup}
              onDeleteMeshGroup={handleDeleteMeshGroup}
              onLayerGroupChange={handleLayerGroupChange}
            />
          </div>

          {!isLayersValid && (
            <div className="flex-shrink-0 px-6 py-2 bg-[#232323] border-t border-gray-800">
              <p className="text-[11px] text-red-400 text-center">
                {t('projects.newProject.validation.illuminatedNormRequired')}
              </p>
            </div>
          )}

          <div
            className={`flex-shrink-0 rounded-bl-2xl bg-[#232323] bottom-0 right-0 w-full h-[60px] backdrop-blur-sm flex justify-between px-6 items-center gap-4 ${isLayersValid ? 'border-t border-gray-800' : ''}`}
          >
            <button
              key="cancel"
              onClick={handleBackClick}
              className={`flex items-center justify-between px-6 py-3 rounded-full font-medium transition-colors duration-200 text-xs ${'border border-[#B3EC49] text-[#B3EC49] hover:border-lime-500'}`}
              style={{
                height: '36px',
                paddingTop: '10px',
                paddingBottom: '10px',
                paddingLeft: '18px',
                paddingRight: '18px',
                gap: '8px',
              }}
            >
              {t('common.back')}
            </button>

            <button
              key="next-step"
              onClick={handleCreate}
              disabled={!isLayersValid}
              className={`flex items-center justify-between px-6 py-3 rounded-full font-medium transition-colors duration-200 text-xs ${'border border-gray-600 hover:border-gray-500 bg-lime-400 text-black font-medium hover:bg-lime-500 transition disabled:opacity-50 disabled:cursor-not-allowed'}`}
              style={{
                height: '36px',
                paddingTop: '10px',
                paddingBottom: '10px',
                paddingLeft: '18px',
                paddingRight: '18px',
                gap: '8px',
              }}
            >
              {t('projects.newProject.finish')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
